<template>
  <div class="reservation-details-display">
    <div class="alert alert-danger" v-if="reservation.completed && reservation.AdminRequests[0] && !reservation.AdminRequests[0].actioned">
      <p><span class="fa fa-fw fa-exclamation-triangle"></span> <strong>Reservation had a problem.</strong></p>
      <hr>
      <p class="mb-1" v-for="problem in reservation.AdminRequests" :key="problem.uuid">
        <router-link v-if="!problem.actioned" class="btn btn-danger btn-sm" :to="{ path: '/follow-up/problems/' + problem.uuid }">
          View {{ problem.requestType }} Problem
        </router-link>
      </p>
    </div>
    <div class="toolbar text-right">
      <dropdown-input label="Car Commands"
        v-if="!reservation.completed && !reservation.cancelled"
        label-icon="fa-wifi"
        :actions="carCommands"
        :dropright="true"
        @action="executeAction($event, reservation.Car)">
      </dropdown-input>
      <dropdown-input label="Reservation Actions"
        label-icon="fa-calendar-alt"
        :actions="reservationActions(reservation, loggedInUser)"
        :dropright="true"
        @action="executeAction($event, reservation)">
      </dropdown-input>
    </div>
    <div class="row">
      <div class="col-sm-4">
        <div class="card card-primary mb-2">
          <div class="card-header">
            Reservation #{{ reservation.reservationId }}
          </div>
          <div class="card-body p-1">
            <reservation-display :data="reservation"></reservation-display>
          </div>
        </div>
        <div class="card card-primary">
          <div class="card-header">
            Car Details
          </div>
          <div class="card-body p-1">
            <car-display :car="reservation.Car" :lot="reservation.Lot"></car-display>
          </div>
        </div>
      </div>
      <div class="col-sm-8">
        <nav-tab :tab-count="5">
          <!-- Car Availability -->
          <div title="Car Availability" slot="tab1" icon="fa-clock">
            <car-availability
              :availability="reservation.availability"
              :car="reservation.Car"
              :date="reservation.startDate"
              @previous="previousAvailability()"
              @next="nextAvailability()"
              @change="changeAvailability($event)"
              :reservation="reservation.reservationId">
            </car-availability>
          </div>
          <!-- Car Message Log -->
          <div title="Car Message Log" slot="tab2" icon="fa-file-alt">
            <div class="card card-primary">
              <div class="card-header">
                Car Message Log
              </div>
              <div class="card-body">
                <search-form v-if="messagesForm"
                  :results="carMessages"
                  :type="carMessagesType"
                  :total-results="carMessagesTotal"
                  :options="messagesForm">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Reservation History -->
          <div title="Reservation History" slot="tab3" icon="fa-calendar-alt">
            <div class="card card-primary">
              <div class="card-header">Reservation History</div>
              <div class="card-body">
                <reservation-history-display
                  :timezone="reservation.Lot.timezone"
                  :history="reservation.history"
                  :completed-end-date="reservation.completedEndDate"
                  :end-date="reservation.endDate">
                </reservation-history-display>
              </div>
            </div>
          </div>
          <!-- Reservation Charges -->
          <div title="Reservation Charges" slot="tab4" icon="fa-dollar-sign">
            <div class="card card-primary">
              <div class="card-header">
                Reservation Charges
              </div>
              <div class="card-body">
                <reservation-costs :reservation="reservation"></reservation-costs>
              </div>
            </div>
          </div>
          <!-- Reservation Notes -->
          <div title="Reservation Notes" slot="tab5" icon="fa-sticky-note">
            <div class="card card-primary">
              <div class="card-header">
                Reservation Notes
              </div>
              <div class="card-body">
                <search-form v-if="notesForm"
                  :results="reservationNotes"
                  :total-results="reservationNotesTotal"
                  :options="notesForm">
                </search-form>
              </div>
            </div>
          </div>
        </nav-tab>
      </div>
    </div>
  </div>
</template>

<script>
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay.vue'
import CarAvailability from '@/components/admin/CarAvailability.vue'
import CarDisplay from '@/components/displays/CarDisplay.vue'
import DropdownInput from '@/components/inputs/DropdownInput'
import NavTab from '@/components/NavTab'
import ReservationCalendar from '@/components/displays/ReservationCalendar'
import ReservationCosts from '@/components/displays/ReservationCosts.vue'
import ReservationDisplay from '@/components/displays/ReservationDisplay.vue'
import ReservationHistoryDisplay from '@/components/displays/ReservationHistoryDisplay'
import SearchForm from '@/components/SearchForm'
import { mapGetters, mapActions } from 'vuex'
import {
  ADD_RESERVATION_NOTE,
  CREATE_RESERVATION_CHARGE,
  LOAD_RESERVATION,
  LOAD_AVAILABILITY,
  LOAD_CAR,
  LOAD_HISTORY,
  LOAD_RESERVATION_BREAKDOWNS,
  INSTANT_RESERVATION,
  UNLOCK_CAR,
  LOCK_CAR,
  IGNITION_ON,
  IGNITION_OFF,
  CAPTURE_CARD,
  IMMOBILIZE_VEHICLE,
  GPS_LOCATION,
  RESEND_RESERVATION,
  EDIT_RESERVATION,
  CANCEL_RESERVATION,
  REFUND_RESERVATION,
  GENERATE_RESERVATION_INVOICE,
  SEND_CANCEL_MESSAGE,
  TRIP_DATA
} from '@/vuex/types'

export default {
  name: 'ReservationDetailsDisplay',
  components: {
    AccountNoteDisplay,
    CarAvailability,
    CarDisplay,
    DropdownInput,
    NavTab,
    ReservationCalendar,
    ReservationCosts,
    ReservationDisplay,
    ReservationHistoryDisplay,
    SearchForm
  },
  computed: {
    messagesForm () {
      if (this.reservation.CarUuid) {
        return {
          permalink: 'getCarMessages',
          params: {
            carUuid: this.reservation.CarUuid,
            reservationId: this.reservation.reservationId
          }
        }
      } else {
        return false
      }
    },
    notesForm () {
      if (this.reservation.uuid) {
        return {
          permalink: 'getReservationNotes',
          params: {
            reservationUuid: this.reservation.uuid
          }
        }
      } else {
        return false
      }
    },
    reservation () {
      return {
        ...this.loadedReservation,
        ...this.updatedReservation
      }
    },
    ...mapGetters([
      'loadedReservation',
      'carCommands',
      'reservationActions',
      'carMessages',
      'carMessagesTotal',
      'carMessagesType',
      'reservationNotes',
      'reservationNotesTotal',
      'loggedInUser'
    ])
  },
  data () {
    return {
      availabilityDate: new Date(),
      updatedReservation: {}
    }
  },
  watch: {
    async $route () {
      await this.$store.dispatch(LOAD_RESERVATION, this.$route.params.reservationUuid)
      await this.$store.dispatch(LOAD_HISTORY)
      await this.$store.dispatch(LOAD_RESERVATION_BREAKDOWNS)
      this.availabilityDate = this.reservation.startDate
      await this.$store.dispatch(LOAD_AVAILABILITY, this.availabilityDate, this.reservation.Car.uuid)
      this.$store.commit(LOAD_CAR, { carUuid: this.reservation.CarUuid })
    }
  },
  async mounted () {
    await this.$store.dispatch(LOAD_RESERVATION, this.$route.params.reservationUuid)
    await this.$store.dispatch(LOAD_HISTORY)
    await this.$store.dispatch(LOAD_RESERVATION_BREAKDOWNS)
    this.availabilityDate = this.reservation.startDate
    await this.$store.dispatch(LOAD_AVAILABILITY, this.availabilityDate, this.reservation.Car.uuid)
    this.$store.commit(LOAD_CAR, { carUuid: this.reservation.CarUuid })
  },
  methods: {
    async previousAvailability () {
      this.availabilityDate = moment(this.availabilityDate).subtract(1, 'days').format()
      await this.$store.dispatch(LOAD_AVAILABILITY, this.availabilityDate)
    },
    async nextAvailability () {
      this.availabilityDate = moment(this.availabilityDate).add(1, 'days').format()
      await this.$store.dispatch(LOAD_AVAILABILITY, this.availabilityDate)
    },
    async changeAvailability (date) {
      this.availabilityDate = moment(date).format()
      await this.$store.dispatch(LOAD_AVAILABILITY, this.availabilityDate)
    },
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedReservation = result.updated
    },
    ...mapActions([
      CREATE_RESERVATION_CHARGE,
      ADD_RESERVATION_NOTE,
      RESEND_RESERVATION,
      INSTANT_RESERVATION,
      UNLOCK_CAR,
      LOCK_CAR,
      IMMOBILIZE_VEHICLE,
      GPS_LOCATION,
      EDIT_RESERVATION,
      CANCEL_RESERVATION,
      REFUND_RESERVATION,
      GENERATE_RESERVATION_INVOICE,
      SEND_CANCEL_MESSAGE,
      TRIP_DATA,
      IGNITION_ON,
      IGNITION_OFF,
      CAPTURE_CARD
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/ReservationDetailsDisplay.vue